import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Avatar,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Grid,
  Paper,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { AccountCircle, Save, Person, Mail } from '@material-ui/icons';
import { useAuth } from '../../contexts/AuthContext';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';

const useStyles = makeStyles((theme) => ({
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: theme.spacing(3),
  },
  card: {
    maxWidth: 800,
    margin: '0 auto',
    borderRadius: 16,
    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
  },
  header: {
    textAlign: 'center',
    marginBottom: theme.spacing(4),
  },
  avatar: {
    width: 80,
    height: 80,
    backgroundColor: theme.palette.primary.main,
    margin: '0 auto',
    marginBottom: theme.spacing(2),
  },
  section: {
    marginBottom: theme.spacing(4),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  formGroup: {
    marginBottom: theme.spacing(2),
  },
  saveButton: {
    background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
    color: 'white',
    fontWeight: 600,
    padding: theme.spacing(1.5, 4),
    fontSize: '1.1rem',
    '&:hover': {
      background: 'linear-gradient(45deg, #5a67d8 30%, #6b46c1 90%)',
    },
  },
  statCard: {
    padding: theme.spacing(2),
    textAlign: 'center',
    background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
    borderRadius: theme.shape.borderRadius,
  },
  statNumber: {
    fontSize: '2rem',
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  statLabel: {
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  input: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
    },
  },
}));

function Account() {
  const classes = useStyles();
  const { currentUser, userData, fetchUserData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    gender: '',
  });

  useEffect(() => {
    if (userData) {
      setFormData({
        name: userData.name || '',
        email: userData.email || '',
        gender: userData.demographics?.gender || '',
      });
    }
  }, [userData]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
    setSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      // Update user document in Firestore
      await updateDoc(doc(db, 'users', currentUser.uid), {
        name: formData.name,
        'demographics.gender': formData.gender,
        updatedAt: new Date(),
      });

      // Refresh user data
      await fetchUserData(currentUser.uid);
      
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!userData) {
    return (
      <Container className={classes.container}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container className={classes.container}>
      <Card className={classes.card}>
        <CardContent style={{ padding: '2rem' }}>
          <div className={classes.header}>
            <Avatar className={classes.avatar}>
              <AccountCircle style={{ fontSize: 48 }} />
            </Avatar>
            <Typography variant="h4" gutterBottom>
              Account Settings
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Manage your profile and preferences
            </Typography>
          </div>

          {/* Account Stats */}
          <div className={classes.section}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Account Overview
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Paper className={classes.statCard}>
                  <Typography className={classes.statNumber}>
                    {userData.tokens || 0}
                  </Typography>
                  <Typography className={classes.statLabel}>
                    Tokens Remaining
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper className={classes.statCard}>
                  <Typography className={classes.statNumber}>
                    {userData.generationsCount || 0}
                  </Typography>
                  <Typography className={classes.statLabel}>
                    Photos Generated
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper className={classes.statCard}>
                  <Typography className={classes.statNumber}>
                    {userData.trainingStatus === 'complete' ? '✓' : '○'}
                  </Typography>
                  <Typography className={classes.statLabel}>
                    Training Status
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </div>

          <Divider />

          {/* Profile Settings */}
          <div className={classes.section}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Profile Information
            </Typography>

            {success && (
              <Alert severity="success" style={{ marginBottom: '1rem' }}>
                Profile updated successfully!
              </Alert>
            )}

            {error && (
              <Alert severity="error" style={{ marginBottom: '1rem' }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <div className={classes.formGroup}>
                <TextField
                  name="name"
                  label="Full Name"
                  variant="outlined"
                  fullWidth
                  value={formData.name}
                  onChange={handleChange}
                  className={classes.input}
                  InputProps={{
                    startAdornment: <Person style={{ marginRight: 8, color: '#666' }} />
                  }}
                />
              </div>

              <div className={classes.formGroup}>
                <TextField
                  name="email"
                  label="Email Address"
                  variant="outlined"
                  fullWidth
                  value={formData.email}
                  disabled
                  className={classes.input}
                  helperText="Email cannot be changed"
                  InputProps={{
                    startAdornment: <Mail style={{ marginRight: 8, color: '#666' }} />
                  }}
                />
              </div>

              <div className={classes.formGroup}>
                <FormControl variant="outlined" fullWidth className={classes.input}>
                  <InputLabel>Gender</InputLabel>
                  <Select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    label="Gender"
                  >
                    <MenuItem value="">
                      <em>Select Gender</em>
                    </MenuItem>
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                    <MenuItem value="prefer-not-to-say">Prefer not to say</MenuItem>
                  </Select>
                </FormControl>
              </div>

              <Box display="flex" justifyContent="center" mt={3}>
                <Button
                  type="submit"
                  variant="contained"
                  className={classes.saveButton}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </form>
          </div>

          <Divider />

          {/* Additional Information */}
          <div className={classes.section}>
            <Typography variant="h6" className={classes.sectionTitle}>
              About Gender Selection
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Gender selection helps us choose the most appropriate photo scenarios and prompts for your generated images. 
              This setting affects how the AI interprets clothing, poses, and styling in the pre-made scenarios.
            </Typography>
          </div>
        </CardContent>
      </Card>
    </Container>
  );
}

export default Account;